export interface MapWorkbenchSpiderifyPoint {
  x: number;
  y: number;
}

export interface MapWorkbenchSpiderifyViewport {
  width: number;
  height: number;
  padding?: number;
}

export interface MapWorkbenchSpiderifyFeatureLike {
  id?: string | number | null;
  source?: string | null;
  sourceLayer?: string | null;
  layerId?: string | null;
  geometry?: Record<string, unknown> | null;
  properties?: Record<string, unknown> | null;
  rawJson?: Record<string, unknown> | null;
}

export interface MapWorkbenchSpiderifyCandidate<T> {
  key: string;
  feature: T;
  point: MapWorkbenchSpiderifyPoint;
  angleRadians: number;
  distancePx: number;
  index: number;
}

export interface BuildMapWorkbenchSpiderifyLayoutArgs<T extends MapWorkbenchSpiderifyFeatureLike> {
  anchor: MapWorkbenchSpiderifyPoint;
  features: T[];
  viewport?: MapWorkbenchSpiderifyViewport | null;
  paddingPx?: number;
  spiralThreshold?: number;
  minRadiusPx?: number;
  ringRadiusStepPx?: number;
  spiralRadiusStepPx?: number;
}

const DEFAULT_PADDING_PX = 24;
const DEFAULT_MIN_RADIUS_PX = 44;
const DEFAULT_RING_RADIUS_STEP_PX = 16;
const DEFAULT_SPIRAL_RADIUS_STEP_PX = 14;
const DEFAULT_SPIRAL_THRESHOLD = 10;
const FULL_CIRCLE_RADIANS = Math.PI * 2;

const clamp = (value: number, min: number, max: number) => Math.min(Math.max(value, min), max);

const stableNormalizeValue = (value: unknown): unknown => {
  if (Array.isArray(value)) {
    return value.map(stableNormalizeValue);
  }
  if (value && typeof value === 'object') {
    return Object.keys(value as Record<string, unknown>)
      .sort()
      .reduce<Record<string, unknown>>((acc, key) => {
        acc[key] = stableNormalizeValue((value as Record<string, unknown>)[key]);
        return acc;
      }, {});
  }
  return value ?? null;
};

const stableStringify = (value: unknown) => JSON.stringify(stableNormalizeValue(value));

const readFeatureGeometry = (feature: MapWorkbenchSpiderifyFeatureLike) =>
  feature.geometry ?? ((feature.rawJson?.geometry as Record<string, unknown> | undefined) ?? null);

const readFeatureProperties = (feature: MapWorkbenchSpiderifyFeatureLike) =>
  feature.properties ?? ((feature.rawJson?.properties as Record<string, unknown> | undefined) ?? null);

export const buildMapWorkbenchSpiderifyFeatureIdentity = (
  feature: MapWorkbenchSpiderifyFeatureLike,
) => {
  const source = typeof feature.source === 'string' ? feature.source.trim() : '';
  const sourceLayer = typeof feature.sourceLayer === 'string' ? feature.sourceLayer.trim() : '';
  if (typeof feature.id === 'number') {
    return `${source}::${sourceLayer}::${String(feature.id)}`;
  }
  if (typeof feature.id === 'string' && feature.id.trim()) {
    return `${source}::${sourceLayer}::${feature.id.trim()}`;
  }
  return [
    source,
    sourceLayer,
    stableStringify(readFeatureGeometry(feature)),
    stableStringify(readFeatureProperties(feature)),
  ].join('::');
};

export const dedupeMapWorkbenchSpiderifyFeatures = <
  T extends MapWorkbenchSpiderifyFeatureLike,
>(
  features: T[],
): Array<{ key: string; feature: T }> => {
  const seen = new Set<string>();
  const deduped: Array<{ key: string; feature: T }> = [];
  features.forEach(feature => {
    const key = buildMapWorkbenchSpiderifyFeatureIdentity(feature);
    if (seen.has(key)) {
      return;
    }
    seen.add(key);
    deduped.push({ key, feature });
  });
  return deduped;
};

export const buildMapWorkbenchSpiderifyCandidateKeys = <
  T extends MapWorkbenchSpiderifyFeatureLike,
>(
  features: T[],
) => dedupeMapWorkbenchSpiderifyFeatures(features).map(entry => entry.key);

export const selectMapWorkbenchSpiderifyFeatures = <
  T extends MapWorkbenchSpiderifyFeatureLike,
>({
  features,
  maxCandidates = Number.POSITIVE_INFINITY,
  predicate,
}: {
  features: T[];
  maxCandidates?: number;
  predicate?: (feature: T, index: number) => boolean;
}) => dedupeMapWorkbenchSpiderifyFeatures(features)
  .map(entry => entry.feature)
  .filter((feature, index) => (predicate ? predicate(feature, index) : true))
  .slice(0, maxCandidates);

export const areMapWorkbenchSpiderifyStacksEquivalent = <
  T extends MapWorkbenchSpiderifyFeatureLike,
>({
  previousAnchor,
  nextAnchor,
  previousFeatures,
  nextFeatures,
  anchorTolerancePx = 6,
}: {
  previousAnchor: MapWorkbenchSpiderifyPoint;
  nextAnchor: MapWorkbenchSpiderifyPoint;
  previousFeatures: T[];
  nextFeatures: T[];
  anchorTolerancePx?: number;
}) => {
  const dx = previousAnchor.x - nextAnchor.x;
  const dy = previousAnchor.y - nextAnchor.y;
  if (Math.sqrt(dx * dx + dy * dy) > anchorTolerancePx) {
    return false;
  }
  const previousKeys = buildMapWorkbenchSpiderifyCandidateKeys(previousFeatures);
  const nextKeys = buildMapWorkbenchSpiderifyCandidateKeys(nextFeatures);
  if (previousKeys.length !== nextKeys.length) {
    return false;
  }
  return previousKeys.every((key, index) => key === nextKeys[index]);
};

export const buildMapWorkbenchSpiderifyLayout = <
  T extends MapWorkbenchSpiderifyFeatureLike,
>({
  anchor,
  features,
  viewport,
  paddingPx = viewport?.padding ?? DEFAULT_PADDING_PX,
  spiralThreshold = DEFAULT_SPIRAL_THRESHOLD,
  minRadiusPx = DEFAULT_MIN_RADIUS_PX,
  ringRadiusStepPx = DEFAULT_RING_RADIUS_STEP_PX,
  spiralRadiusStepPx = DEFAULT_SPIRAL_RADIUS_STEP_PX,
}: BuildMapWorkbenchSpiderifyLayoutArgs<T>): MapWorkbenchSpiderifyCandidate<T>[] => {
  const uniqueFeatures = dedupeMapWorkbenchSpiderifyFeatures(features);
  const count = uniqueFeatures.length;
  if (!count) {
    return [];
  }

  return uniqueFeatures.map(({ key, feature }, index) => {
    let angleRadians = -Math.PI / 2;
    let distancePx = minRadiusPx;

    if (count <= spiralThreshold) {
      const ringIndex = Math.floor(index / 8);
      const ringCount = Math.min(8, count - ringIndex * 8);
      const ringOffset = index - ringIndex * 8;
      angleRadians = -Math.PI / 2 + (FULL_CIRCLE_RADIANS * ringOffset) / Math.max(ringCount, 1);
      distancePx = minRadiusPx + ringIndex * ringRadiusStepPx + Math.max(0, count - 6) * 2;
    } else {
      angleRadians = -Math.PI / 2 + index * 0.72;
      distancePx = minRadiusPx + index * spiralRadiusStepPx;
    }

    const rawX = anchor.x + Math.cos(angleRadians) * distancePx;
    const rawY = anchor.y + Math.sin(angleRadians) * distancePx;
    const point = viewport
      ? {
          x: clamp(rawX, paddingPx, Math.max(paddingPx, viewport.width - paddingPx)),
          y: clamp(rawY, paddingPx, Math.max(paddingPx, viewport.height - paddingPx)),
        }
      : { x: rawX, y: rawY };

    return {
      key,
      feature,
      point,
      angleRadians,
      distancePx,
      index,
    };
  });
};
